/**
 * <pravaa-rail>
 * Mobile swipe rail for the "New in Store" section.
 * - Drives the thin progress bar under the rail.
 * - Autoplays on mobile (and on desktop when the section sets [desktop-rail] because there are
 *   more products than columns): advances one card at a time and rewinds at the end.
 *   Pauses while the shopper interacts or hovers, when the rail is off-screen, or the tab is hidden,
 *   and never autoplays for visitors who prefer reduced motion.
 *
 * Built as a custom element so it initialises however the markup arrives
 * (full page load, theme editor re-render, or dev-server hot reload).
 */
const MOBILE_QUERY = '(max-width: 749px)';
const INTERVAL = 3500;
const RESUME_AFTER = 5000;

class PravaaRail extends HTMLElement {
  #timer = null;
  #resumeTimer = null;
  #inView = false;
  #interacting = false;
  #observer = null;
  #abort = null;
  #mobile = window.matchMedia(MOBILE_QUERY);
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    this.rail = this.querySelector('.pravaa-grid');
    this.bar = this.querySelector('.pravaa-progress');
    if (!this.rail) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const passive = { passive: true, signal };

    this.rail.addEventListener('scroll', this.#updateProgress, passive);
    window.addEventListener('resize', this.#updateProgress, passive);

    this.rail.addEventListener('pointerdown', this.#pause, passive);
    this.rail.addEventListener('touchstart', this.#pause, passive);
    this.rail.addEventListener('wheel', this.#pauseBriefly, passive);
    this.addEventListener('mouseenter', this.#pause, passive);
    this.addEventListener('mouseleave', this.#resumeLater, passive);
    for (const type of ['pointerup', 'pointercancel', 'touchend', 'touchcancel']) {
      this.rail.addEventListener(type, this.#resumeLater, passive);
    }

    document.addEventListener('visibilitychange', this.#start, { signal });
    this.#mobile.addEventListener('change', this.#start, { signal });
    this.#reducedMotion.addEventListener('change', this.#start, { signal });

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        this.#inView = entry.isIntersecting;
        this.#start();
      },
      { threshold: 0.25 }
    );
    this.#observer.observe(this.rail);

    this.#updateProgress();
  }

  disconnectedCallback() {
    this.#stop();
    clearTimeout(this.#resumeTimer);
    this.#observer?.disconnect();
    this.#abort?.abort();
  }

  get #cards() {
    return [...this.rail.querySelectorAll('.pravaa-card')].filter((card) => card.offsetParent !== null);
  }

  #updateProgress = () => {
    if (!this.bar) return;
    const { scrollWidth, clientWidth, scrollLeft } = this.rail;
    const max = scrollWidth - clientWidth;
    const visible = scrollWidth ? Math.min(clientWidth / scrollWidth, 1) : 1;
    const progress = max > 0 ? scrollLeft / max : 0;
    this.bar.style.setProperty('--pravaa-thumb', `${visible * 100}%`);
    // translateX % is relative to the thumb itself, so scale by the remaining track
    this.bar.style.setProperty('--pravaa-offset', `${progress * ((1 - visible) / visible) * 100}%`);
  };

  #advance = () => {
    const cards = this.#cards;
    if (cards.length < 2) return;

    const inset = parseFloat(getComputedStyle(this.rail).paddingLeft) || 0;
    const { scrollLeft, scrollWidth, clientWidth } = this.rail;
    const atEnd = scrollLeft >= scrollWidth - clientWidth - 4;
    const next = atEnd ? cards[0] : cards.find((card) => card.offsetLeft - inset > scrollLeft + 4) ?? cards[0];

    this.rail.scrollTo({ left: next.offsetLeft - inset, behavior: 'smooth' });
  };

  #canPlay() {
    return (
      (this.#mobile.matches || this.hasAttribute('desktop-rail')) &&
      !this.#reducedMotion.matches &&
      this.#inView &&
      !this.#interacting &&
      !document.hidden &&
      this.rail.scrollWidth > this.rail.clientWidth
    );
  }

  #start = () => {
    this.#stop();
    if (this.#canPlay()) this.#timer = setInterval(this.#advance, INTERVAL);
  };

  #stop() {
    clearInterval(this.#timer);
    this.#timer = null;
  }

  #pause = () => {
    this.#interacting = true;
    clearTimeout(this.#resumeTimer);
    this.#stop();
  };

  #resumeLater = () => {
    clearTimeout(this.#resumeTimer);
    this.#resumeTimer = setTimeout(() => {
      this.#interacting = false;
      this.#start();
    }, RESUME_AFTER);
  };

  #pauseBriefly = () => {
    this.#pause();
    this.#resumeLater();
  };
}

if (!customElements.get('pravaa-rail')) {
  customElements.define('pravaa-rail', PravaaRail);
}
